import { FC, useEffect, useRef } from "react";
import { locale } from "src/config/app-config";
import {
  useShouldScrollToFooter,
  useHomePageMounted,
} from "src/hooks/stores/use-app-store";
import classNames from "classnames";
import { Divider } from "src/atoms/Divider";
import styles from "./Footer.module.scss";
import { FooterHighlight } from "./FooterHighlight";
import { FooterLinks } from "./FooterLinks";
import { FooterButtons } from "./FooterButtons";
import { FooterBottom } from "./FooterBottom";
import { FooterFeatures } from "./FooterFeatures";
import FooterOptIn from "./FooterOptIn";
import { useSegment } from "src/hooks/useSegment";
import NoSSR from "src/atoms/NoSSR";
import { RRPFootnote } from "src/molecules/RRPFootnote";

interface Props {
  className?: string;
  /** Set by the page layout for routes that show strikethrough RRP prices. */
  showRRPFootnote?: boolean;
}

const Footer: FC<Props> = ({ className, showRRPFootnote }) => {
  const shouldScrollToFooter = useShouldScrollToFooter();
  const homePageMounted = useHomePageMounted();
  const footerRef = useRef<HTMLElement | null>(null);
  const { segmentOptInDeeplinkViewed } = useSegment();

  useEffect(() => {
    if (homePageMounted && shouldScrollToFooter && footerRef.current) {
      const footerPosition =
        (footerRef.current?.getBoundingClientRect()?.top ?? 0) + window.scrollY;
      window.scrollTo({ top: footerPosition - 50, behavior: "smooth" });
      void segmentOptInDeeplinkViewed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homePageMounted, shouldScrollToFooter]);

  return (
    <footer ref={footerRef} className={classNames(styles.wrapper, className)}>
      {showRRPFootnote && <RRPFootnote />}
      <NoSSR>
        <FooterOptIn />
      </NoSSR>
      <FooterHighlight locale={locale} />
      <FooterFeatures locale={locale} />
      <div>
        <div className={styles.middleWrapper}>
          <FooterLinks />
          <FooterButtons />
        </div>
        <Divider />
        <FooterBottom locale={locale} />
      </div>
    </footer>
  );
};

export default Footer;
